"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { ArrowRight, ArrowUpRight, Code2, ExternalLink, Star, GitFork } from "@/components/icons";
import { EyebrowBadge, HeroGlow } from "@/components/ui/";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import {
  GITHUB_URL,
  ROUTES,
  FREE_TRIAL_URL,
  FREE_TRIAL_LABEL,
  YELLOW,
  GREEN,
} from "@/lib/constants";
import FaqSection from "@/components/sections/products/shared/faq";
import BadgesSection from "@/components/sections/shared/badges";
import type { FaqItem } from "@/components/sections/products/shared/faq/types";
import type { GithubRepoStats } from "@/lib/github-stats";
import { projects } from "./data";
import type { OpenSourceProject } from "./data";

const openSourceFaqs: FaqItem[] = [
  {
    q: "Are Proxidize open-source tools free to use?",
    a: "Yes. Proxidize open-source projects are free to use and available on GitHub. You can download them, modify them, and use them as a starting point for your own scraping or automation workflows.",
  },
  {
    q: "Do I need Proxidize proxies to use these tools?",
    a: "No. The tools can be used without Proxidize proxies. However, using proxies can help when you need to handle rate limits, keep large scraping jobs running, or scale bigger data-collection tasks.",
  },
  {
    q: "Which proxies work best with these projects?",
    a: (
      <>
        It depends on the use case.{" "}
        <Link href={ROUTES.productMobileProxies} className="underline hover:text-[--brand]">
          Mobile proxies
        </Link>{" "}
        are usually better for workflows that need trusted premium pooled mobile IPs, sticky
        sessions, or market research.{" "}
        <Link href={ROUTES.productResidentialProxies} className="underline hover:text-[--brand]">
          Residential proxies
        </Link>{" "}
        are often better for broad web scraping, SEO monitoring, price tracking, and data collection
        across many locations.
      </>
    ),
  },
  {
    q: "Can I use these tools for production scraping?",
    a: "Yes, but you should review and adapt the code for your own production requirements. For larger workloads, you may need proxy rotation, retries, logging, queue management, CAPTCHA handling, and compliance checks.",
  },
  {
    q: "Are these tools maintained by Proxidize?",
    a: "Yes. These projects are maintained by Proxidize as open-source tools for developers, scraping teams, and automation engineers.",
  },
  {
    q: "Can I contribute to the projects?",
    a: "Yes. Developers can open issues, suggest improvements, or submit pull requests through GitHub.",
  },
  {
    q: "Are these tools intended for ethical scraping?",
    a: "Yes. These tools are intended for lawful and ethical data collection. Always respect website terms, robots.txt rules, rate limits, privacy laws, and applicable regulations.",
  },
];

function FeatureItem({ text }: { text: string }) {
  return (
    <li className="text-muted-foreground flex items-start gap-2 text-sm">
      <span
        className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: "#5b8cff" }}
        aria-hidden="true"
      />
      {text}
    </li>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="border-foreground/10 bg-foreground/[0.06] text-foreground/60 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium">
      {children}
    </span>
  );
}

function ProjectCard({
  project,
  index,
  stats,
}: {
  project: OpenSourceProject;
  index: number;
  stats?: GithubRepoStats | null;
}) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={scrollRevealViewport}
      className="border-foreground/[0.08] bg-foreground/[0.03] flex flex-col rounded-2xl border p-8 lg:p-10"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Code2 size={18} className="text-[--green]" aria-hidden="true" />
          <h2 className="text-xl font-semibold text-[--brand]">{project.name}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {stats && (
            <>
              <span
                className="text-brand-yellow light:text-blue-600 flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
                style={{
                  backgroundColor: `${YELLOW}1A`,
                  border: `1px solid ${YELLOW}40`,
                }}
              >
                <Star size={12} fill="currentColor" aria-hidden="true" />
                {stats.stars.toLocaleString("en-US")}
              </span>
              <span
                className="text-brand-green flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
                style={{
                  backgroundColor: `${GREEN}1A`,
                  border: `1px solid ${GREEN}40`,
                }}
              >
                <GitFork size={12} aria-hidden="true" />
                {stats.forks.toLocaleString("en-US")}
              </span>
            </>
          )}
          <Badge>{project.language}</Badge>
          <Badge>{project.license}</Badge>
        </div>
      </div>

      <p className="mt-2 text-sm font-medium text-[--green]">{project.tagline}</p>
      <p className="text-muted-foreground mt-3 text-sm leading-relaxed">{project.description}</p>

      <ul className="mt-5 space-y-2.5" aria-label={`${project.name} features`}>
        {project.features.map((feature) => (
          <FeatureItem key={feature} text={feature} />
        ))}
      </ul>

      <div className="mt-auto flex flex-wrap items-center gap-3 pt-8">
        <Link
          href={project.githubUrl}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="bg-primary text-primary-foreground inline-flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
        >
          View on GitHub
          <ArrowUpRight size={14} aria-hidden="true" />
        </Link>
        {project.blogUrl && (
          <Link
            href={project.blogUrl}
            className="text-muted-foreground border-foreground/10 hover:border-foreground/20 inline-flex items-center gap-2 rounded-lg border px-5 py-2 text-sm font-medium transition-colors hover:text-[--brand]"
          >
            Read the blog post
            <ExternalLink size={13} aria-hidden="true" />
          </Link>
        )}
        {project.pageUrl && project.pageLabel && (
          <Link
            href={project.pageUrl}
            className="text-muted-foreground inline-flex items-center gap-2 rounded-lg border border-white/10 px-5 py-2 text-sm font-medium transition-colors hover:border-white/20 hover:text-[--brand]"
          >
            {project.pageLabel}
            <ArrowRight size={13} aria-hidden="true" />
          </Link>
        )}
      </div>
    </motion.div>
  );
}

interface Props {
  githubStats?: Record<string, GithubRepoStats | null>;
}

export default function OpenSourceSection({ githubStats }: Props) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <main id="main-content" tabIndex={-1}>
      <section className="border-border relative overflow-y-clip border-b pt-32 pb-24">
        <HeroGlow align="center" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12 text-center lg:mb-16"
          >
            <EyebrowBadge>Open Source</EyebrowBadge>
            <h1 className="text-foreground mt-4 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Built in the open
            </h1>
            <p className="text-muted-foreground light:text-foreground/80 mx-auto mt-5 max-w-2xl text-lg">
              Proxidize maintains free, open-source tools for the developer community — scraping
              tools and an MCP server for AI agents. Use them standalone or pair them with Proxidize
              proxies for reliable, unblocked data collection at scale.
            </p>
            <span className="cta-glow-btn-wrap mt-6">
              <Link
                href={GITHUB_URL}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="cta-glow-btn-inner"
              >
                github.com/proxidize
              </Link>
            </span>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
            {projects.map((project, i) => (
              <ProjectCard
                key={project.id}
                project={project}
                index={i}
                stats={githubStats?.[project.id]}
              />
            ))}
          </div>
        </div>
      </section>
      {/* Why we build open source */}
      <section className="border-border border-b py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <p className="text-brand-yellow light:text-blue-600 mb-3 text-xs font-semibold tracking-widest uppercase">
            Our philosophy
          </p>
          <h2 className="text-2xl font-bold tracking-tight text-[--brand] sm:text-3xl">
            Why we build open source
          </h2>
          <div className="text-muted-foreground mt-8 space-y-4 text-base leading-relaxed">
            <p>
              Proxidize builds open-source tools to help developers test, prototype, and understand
              real-world data collection and proxy-management workflows before scaling them.
            </p>
            <p>
              Our tools are built around practical scraping problems like rate limits, IP blocks,
              retries, session handling, CAPTCHA challenges, and structured data exports.
            </p>
            <p>
              You can use these projects for free, modify them for your own workflow, or use them as
              a starting point for larger scraping and automation systems. When your workload grows,
              Proxidize helps you scale those workflows with managed{" "}
              <Link
                href={ROUTES.productMobileProxies}
                className="hover:text-foreground text-[--brand] underline underline-offset-2"
              >
                mobile
              </Link>{" "}
              and{" "}
              <Link
                href={ROUTES.productResidentialProxies}
                className="hover:text-foreground text-[--brand] underline underline-offset-2"
              >
                residential proxies
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Scale with Proxidize */}
      <section className="border-border border-b py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <div className="border-foreground/[0.08] bg-foreground/[0.03] rounded-2xl border p-8 lg:p-12">
            <p className="mb-3 text-xs font-semibold tracking-widest text-[--green] uppercase">
              Scale your workflow
            </p>
            <div className="grid gap-8 lg:grid-cols-2 lg:gap-16">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-[--brand] sm:text-3xl">
                  Scale open-source scraping with Proxidize
                </h2>
                <div className="mt-6 flex flex-wrap gap-3">
                  <span className="cta-glow-btn-wrap">
                    <Link href={FREE_TRIAL_URL} className="cta-glow-btn-inner !h-10 !px-5 !text-sm">
                      {FREE_TRIAL_LABEL}
                    </Link>
                  </span>
                  <Link
                    href={ROUTES.pricing}
                    className="text-muted-foreground border-foreground/10 hover:border-foreground/20 inline-flex items-center gap-2 rounded-lg border px-5 py-2 text-sm font-medium transition-colors hover:text-[--brand]"
                  >
                    View proxies
                  </Link>
                </div>
              </div>
              <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
                <p>
                  Most scraping projects start simple. But as request volume grows, developers often
                  run into rate limits, IP blocks, CAPTCHA challenges, and unstable sessions.
                </p>
                <p>
                  Proxidize helps you scale scraping workflows with managed mobile and residential
                  proxies, rotating and sticky sessions, city-level targeting, and support for
                  HTTP(S) and SOCKS5.
                </p>
                <p>
                  Use the open-source tools to prototype your workflow, then connect Proxidize
                  proxies when you need more reliable infrastructure for production.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BadgesSection />

      <FaqSection
        faqs={openSourceFaqs}
        description="Common questions about Proxidize open-source scraping tools."
        headingId="open-source-faq-heading"
        hideContactLink={true}
      />
    </main>
  );
}
